// src/pages/Trend/components/analysis/consumer-reaction/Summary.js
import React, { memo } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { ErrorMessage } from '../../../../../components/common/UI';
import './Summary.css';

// BBC Style Configuration
const BBC_STYLE = {
  FONT_FAMILY: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  PRIMARY_COLOR: '#1380A1',
  SECONDARY_COLOR: '#FAAB18',
  HIGHLIGHT_COLOR: '#D55E00',
  POSITIVE_COLOR: '#2E7D32',
  NEGATIVE_COLOR: '#B71C1C',
  NEUTRAL_COLOR: '#6b7280',
  TEXT_COLOR: '#333333',
  LIGHT_TEXT: '#666666'
};

// Render filter label with monospace number for alignment
const renderFilterLabel = (label) => {
  // Match leading number (e.g., "1" from "1번 필터" or "Filter 1")
  const match = label.match(/^(\d+)(.*)$/);
  if (match) {
    return (
      <>
        <span className="filter-label-number">{match[1]}</span>
        {match[2]}
      </>
    );
  }
  return label;
};

// Normalize direction value
const normalizeDirection = (direction) => {
  const d = direction?.toLowerCase();
  if (d === '상승' || d === 'increasing' || d === 'up') return 'up';
  if (d === '하락' || d === 'decreasing' || d === 'down') return 'down';
  return 'stable';
};

// Get direction color
const getDirectionColor = (direction) => {
  const normalized = normalizeDirection(direction);
  if (normalized === 'up') return BBC_STYLE.POSITIVE_COLOR;
  if (normalized === 'down') return BBC_STYLE.NEGATIVE_COLOR;
  return BBC_STYLE.NEUTRAL_COLOR;
};

// Create narrative title based on data insights
const createNarrativeTitle = (primary, secondary, hasSecondaryData, translate) => {
  // Helper function to find data by multiple possible keys
  const findDataByKeys = (obj, keys) => {
    for (const key of keys) {
      if (obj[key]) return obj[key];
    }
    return null;
  };

  const marketResponseKeys = ['시장 반응', 'Market Response', 'marketResponse'];
  const satisfactionKeys = ['소비자 만족도', 'Consumer Satisfaction', 'consumerSatisfaction'];
  const engagementKeys = ['소비자 참여도', 'Consumer Engagement', 'consumerEngagement'];

  const marketResponse = findDataByKeys(primary, marketResponseKeys);
  const satisfaction = findDataByKeys(primary, satisfactionKeys);
  const engagement = findDataByKeys(primary, engagementKeys);

  const marketDir = normalizeDirection(marketResponse?.direction);
  const satDir = normalizeDirection(satisfaction?.direction);
  const engDir = normalizeDirection(engagement?.direction);

  // Count positive signals
  const positiveCount = [marketDir, satDir, engDir].filter(d => d === 'up').length;
  const negativeCount = [marketDir, satDir, engDir].filter(d => d === 'down').length;

  if (!hasSecondaryData) {
    // Single filter narrative
    let overallSentiment;
    let sentimentColor;

    if (positiveCount >= 2) {
      overallSentiment = translate('trendPage.consumerReaction.narrative.positive');
      sentimentColor = BBC_STYLE.POSITIVE_COLOR;
    } else if (negativeCount >= 2) {
      overallSentiment = translate('trendPage.consumerReaction.narrative.negative');
      sentimentColor = BBC_STYLE.NEGATIVE_COLOR;
    } else {
      overallSentiment = translate('trendPage.consumerReaction.narrative.mixed');
      sentimentColor = BBC_STYLE.HIGHLIGHT_COLOR;
    }

    return (
      <h2 style={{
        fontSize: '1.1rem',
        fontWeight: '600',
        color: BBC_STYLE.TEXT_COLOR,
        margin: '0 0 8px 0',
        lineHeight: '1.4'
      }}>
        {translate('trendPage.consumerReaction.narrative.overallPrefix')}{' '}
        <span style={{
          fontWeight: '700',
          color: sentimentColor,
          borderBottom: `2px solid ${sentimentColor}`,
          paddingBottom: '1px'
        }}>
          {overallSentiment}
        </span>
      </h2>
    );
  }

  // Comparison mode
  const marketResponse2 = findDataByKeys(secondary, marketResponseKeys);
  const satisfaction2 = findDataByKeys(secondary, satisfactionKeys);
  const engagement2 = findDataByKeys(secondary, engagementKeys);

  const marketDir2 = normalizeDirection(marketResponse2?.direction);
  const satDir2 = normalizeDirection(satisfaction2?.direction);
  const engDir2 = normalizeDirection(engagement2?.direction);

  const positiveCount2 = [marketDir2, satDir2, engDir2].filter(d => d === 'up').length;

  const isBetter = positiveCount > positiveCount2;
  const isSame = positiveCount === positiveCount2;

  return (
    <h2 style={{
      fontSize: '1.1rem',
      fontWeight: '600',
      color: BBC_STYLE.TEXT_COLOR,
      margin: '0 0 8px 0',
      lineHeight: '1.4'
    }}>
      {isSame ? (
        <>
          {translate('trendPage.consumerReaction.narrative.comparisonSame')}{' '}
          <span style={{
            fontWeight: '700',
            color: BBC_STYLE.HIGHLIGHT_COLOR
          }}>
            {translate('trendPage.consumerReaction.narrative.similar')}
          </span>
        </>
      ) : (
        <>
          <span style={{
            fontWeight: '700',
            color: isBetter ? BBC_STYLE.PRIMARY_COLOR : BBC_STYLE.SECONDARY_COLOR
          }}>
            {isBetter ? translate('trendPage.filters.set1') : translate('trendPage.filters.set2')}
          </span>
          {' '}{translate('trendPage.consumerReaction.narrative.comparisonBetter')}
        </>
      )}
    </h2>
  );
};

// Create subtitle with metric details
const createSubtitle = (primary, secondary, hasSecondaryData, translate) => {
  const findDataByKeys = (obj, keys) => {
    for (const key of keys) {
      if (obj[key]) return obj[key];
    }
    return null;
  };

  const marketResponseKeys = ['시장 반응', 'Market Response', 'marketResponse'];
  const satisfactionKeys = ['소비자 만족도', 'Consumer Satisfaction', 'consumerSatisfaction'];
  const engagementKeys = ['소비자 참여도', 'Consumer Engagement', 'consumerEngagement'];

  const renderMetricItem = (data, label, filterColor = null) => {
    const marketResponse = findDataByKeys(data, marketResponseKeys);
    const satisfaction = findDataByKeys(data, satisfactionKeys);
    const engagement = findDataByKeys(data, engagementKeys);

    const renderDirection = (direction) => {
      const normalized = normalizeDirection(direction);
      const color = getDirectionColor(direction);
      const text = translate(`trendPage.consumerReaction.directions.${normalized}`);
      return (
        <span style={{ fontWeight: '600', color }}>
          {text}
        </span>
      );
    };

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap'
      }}>
        {filterColor && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginRight: '4px'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: filterColor
            }} />
            <span style={{ fontWeight: '600', color: filterColor }}>{renderFilterLabel(label)}</span>
          </div>
        )}
        <span style={{ color: BBC_STYLE.LIGHT_TEXT }}>
          {translate('trendPage.consumerReaction.metrics.marketResponse')}:{' '}
        </span>
        {renderDirection(marketResponse?.direction)}
        <span style={{ color: '#D0D0D0', margin: '0 4px' }}>|</span>
        <span style={{ color: BBC_STYLE.LIGHT_TEXT }}>
          {translate('trendPage.consumerReaction.metrics.consumerSatisfaction')}:{' '}
        </span>
        {renderDirection(satisfaction?.direction)}
        <span style={{ color: '#D0D0D0', margin: '0 4px' }}>|</span>
        <span style={{ color: BBC_STYLE.LIGHT_TEXT }}>
          {translate('trendPage.consumerReaction.metrics.consumerEngagement')}:{' '}
        </span>
        {renderDirection(engagement?.direction)}
      </div>
    );
  };

  if (!hasSecondaryData) {
    return (
      <div style={{
        fontSize: '0.85rem',
        color: BBC_STYLE.TEXT_COLOR,
        margin: '0 0 20px 0',
        lineHeight: '1.5'
      }}>
        {renderMetricItem(primary)}
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      fontSize: '0.85rem',
      color: BBC_STYLE.TEXT_COLOR,
      margin: '0 0 20px 0',
      lineHeight: '1.5'
    }}>
      {renderMetricItem(primary, translate('trendPage.filters.set1'), BBC_STYLE.PRIMARY_COLOR)}
      {renderMetricItem(secondary, translate('trendPage.filters.set2'), BBC_STYLE.SECONDARY_COLOR)}
    </div>
  );
};

const Summary = ({ data }) => {
  const { translate } = useLanguage();

  if (!data?.primary?.summary) {
    return <ErrorMessage message={translate('trendPage.consumerReaction.errors.noSummaryData')} />;
  }

  const { primary, secondary } = data;
  const hasSecondaryData = secondary?.summary !== undefined;

  if (!primary.summary) {
    return <ErrorMessage message={translate('trendPage.consumerReaction.errors.invalidSummaryData')} />;
  }

  return (
    <div className="summary-narrative-container" style={{ fontFamily: BBC_STYLE.FONT_FAMILY }}>
      {createNarrativeTitle(primary.summary, secondary?.summary, hasSecondaryData, translate)}
      {createSubtitle(primary.summary, secondary?.summary, hasSecondaryData, translate)}
    </div>
  );
};

export default memo(Summary);
